import type { ChartConfig } from "@lawline/ui/components/chart";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@lawline/ui/components/chart";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";

import type { UsageBucket, UsageSeriesPoint } from "../usage.types";
import { formatBucket, formatTokens } from "../usage.utils";

export interface ChartSeries {
	// dataKey into UsageSeriesPoint.
	key: string;
	color: string;
	label: string;
}

interface UsageAreaChartProps {
	points: UsageSeriesPoint[];
	bucket: UsageBucket;
	series: ChartSeries[];
	// Stack the series into one cumulative area (tokens); leave unset for a single series.
	stacked?: boolean;
}

export function UsageAreaChart({ points, bucket, series, stacked }: UsageAreaChartProps) {
	// Drives --color-* vars + tooltip labels, keyed by dataKey.
	const config: ChartConfig = Object.fromEntries(
		series.map((s) => [s.key, { label: s.label, color: s.color }]),
	);

	return (
		<ChartContainer config={config} className="aspect-auto h-[320px] w-full">
			<AreaChart data={points} margin={{ left: 4, right: 12, top: 8, bottom: 4 }}>
				<CartesianGrid vertical={false} strokeDasharray="3 3" />
				<XAxis
					dataKey="t"
					tickLine={false}
					axisLine={false}
					tickMargin={8}
					minTickGap={24}
					tick={{ fontSize: 11 }}
					tickFormatter={(v) => formatBucket(String(v), bucket)}
				/>
				<YAxis
					tickLine={false}
					axisLine={false}
					width={44}
					tick={{ fontSize: 11 }}
					tickFormatter={(v) => formatTokens(Number(v))}
				/>
				<ChartTooltip
					cursor={{ strokeDasharray: "3 3" }}
					content={
						<ChartTooltipContent
							className="min-w-[12rem]"
							labelFormatter={(_label, payload) => {
								const t = (payload?.[0]?.payload as UsageSeriesPoint | undefined)?.t;
								return t ? formatBucket(t, bucket) : "";
							}}
							formatter={(value, name, item) => (
								<div className="flex flex-1 items-center justify-between gap-8">
									<span className="text-muted-foreground flex items-center gap-1.5">
										<span className="size-2 rounded-[2px]" style={{ background: item.color }} />
										{config[String(name)]?.label ?? name}
									</span>
									<span className="text-foreground font-mono font-medium tabular-nums">
										{Number(value).toLocaleString("en")}
									</span>
								</div>
							)}
						/>
					}
				/>
				{series.map((s) => (
					<Area
						key={s.key}
						dataKey={s.key}
						type="monotone"
						stackId={stacked ? "stack" : undefined}
						stroke={s.color}
						fill={s.color}
						fillOpacity={0.2}
						strokeWidth={2}
					/>
				))}
			</AreaChart>
		</ChartContainer>
	);
}
